import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  CreditCardIcon,
  NoSymbolIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(value))
  : '—';

const formatPrice = (cents) => `${(Number(cents) / 100).toLocaleString('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

const SubscriptionPage = () => {
  const [status, setStatus] = useState(null);
  const [members, setMembers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [periodicite, setPeriodicite] = useState('MENSUEL');
  const [promoCode, setPromoCode] = useState('');
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ surnom: '', email: '', motDePasse: '' });
  const [demoForm, setDemoForm] = useState({
    titulaireType: 'PERSONNE', titulaireNom: '', raisonSociale: '', email: '', telephone: '',
    adresseLigne1: '', adresseLigne2: '', codePostal: '', ville: '', pays: 'FR',
    numeroCarte: '4242 4242 4242 4242', expirationMois: '12',
    expirationAnnee: String(new Date().getFullYear() + 2), cryptogramme: '123',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const isProfessional = status?.CompteType === 'PROFESSIONNEL';
  const isOwner = Boolean(status?.EstComptePrincipal && isProfessional);
  const canManageSubscription = isOwner || [3, 4].includes(Number(status?.GradeID));
  const plan = isProfessional ? 'PROFESSIONNEL' : 'PREMIUM';
  const memberMax = Number(status?.DroitsCompte?.ProMemberMax ?? 0);
  const activityJournalScope = Number(status?.Droits?.ActivityJournalScope || 0);
  const monthlyPriceCents = Number(status?.OffreCommerciale?.PriceMonthlyCents || 0);
  const annualDiscountPercent = Number(status?.OffreCommerciale?.AnnualDiscountPercent || 0);
  const annualBasePriceCents = monthlyPriceCents * 12;
  const annualStandardPriceCents = Math.round(
    annualBasePriceCents * (100 - annualDiscountPercent) / 100
  );
  const standardPriceCents = periodicite === 'ANNUEL'
    ? annualStandardPriceCents
    : monthlyPriceCents;
  const activePromoPreview = promoPreview?.PeriodiciteSelectionnee === periodicite ? promoPreview : null;
  const simulatedPriceCents = Number(activePromoPreview?.PrixFinalCentimes ?? standardPriceCents);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const statusResponse = await api.get('/account/subscription');
      const accountStatus = statusResponse.data;
      setStatus(accountStatus);
      const canManage = Boolean(
        (accountStatus?.CompteType === 'PROFESSIONNEL' && accountStatus?.EstComptePrincipal)
        || [3, 4].includes(Number(accountStatus?.GradeID))
      );
      if (accountStatus?.CompteType === 'PROFESSIONNEL' && accountStatus?.EstComptePrincipal) {
        const membersResponse = await api.get('/account/members');
        setMembers(membersResponse.data || []);
      }
      if (canManage) {
        const methodsResponse = await api.get('/account/payment-methods');
        const methods = methodsResponse.data || [];
        setPaymentMethods(methods);
        setSelectedPaymentMethodId(String(methods.find((item) => item.EstParDefaut)?.MoyenPaiementID || methods[0]?.MoyenPaiementID || ''));
      }
      if (Number(accountStatus?.Droits?.ActivityJournalScope || 0) > 0) {
        const historyResponse = await api.get('/account/history?limit=50');
        setHistory(historyResponse.data?.items || []);
      } else {
        setHistory([]);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Impossible de charger les informations du compte.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remainingDays = useMemo(() => {
    if (!status?.Abonnement?.FinDate) return 0;
    return Math.max(0, Math.ceil((new Date(status.Abonnement.FinDate).getTime() - Date.now()) / 86400000));
  }, [status]);

  const perform = async (action) => {
    setError('');
    setMessage('');
    try {
      const response = await action();
      setMessage(response.data?.message || 'Modification enregistrée.');
      await load();
      return true;
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Une erreur est survenue.');
      return false;
    }
  };

  const validatePromoCode = async () => {
    setPromoLoading(true);
    setError('');
    setMessage('');
    setPromoPreview(null);
    try {
      const response = await api.post('/account/promo-code/validate', {
        codePromo: promoCode,
        periodicite,
      });
      setPromoPreview({ ...response.data, PeriodiciteSelectionnee: periodicite });
      setPromoCode(response.data.Code);
      setMessage(`${response.data.message} Aucun débit réel : le tarif reste une simulation.`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Ce code promotionnel ne peut pas être utilisé.');
    } finally {
      setPromoLoading(false);
    }
  };

  const simulatePayment = async () => {
    const success = await perform(() => api.post('/account/subscription/fake', {
      plan,
      periodicite,
      moyenPaiementId: Number(selectedPaymentMethodId),
      codePromo: activePromoPreview?.Code || undefined,
    }));
    if (success) {
      setPromoCode('');
      setPromoPreview(null);
    }
  };

  const createMember = async (event) => {
    event.preventDefault();
    await perform(() => api.post('/account/members', form));
    setForm({ surnom: '', email: '', motDePasse: '' });
  };

  const createDemoPaymentMethod = async (event) => {
    event.preventDefault();
    await perform(() => api.post('/account/payment-methods/demo', demoForm));
  };

  if (loading) return <p className="text-slate-300">Chargement du compte…</p>;

  return (
    <main className="mx-auto max-w-6xl space-y-6 text-slate-100">
      <header>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-300">Compte V2</p>
        <h1 className="mt-2 text-3xl font-black text-white">Abonnement et droits</h1>
        <p className="mt-2 text-sm text-slate-400">Consultez les limites du compte et simulez le futur parcours de paiement.</p>
      </header>

      {message && <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-100">{message}</p>}
      {error && <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-rose-100">{error}</p>}

      <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <article className="rounded-xl border border-sky-500/25 bg-slate-950/70 p-6 shadow-xl shadow-sky-950/30">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-sky-300">{isProfessional ? 'Professionnel' : status?.GradeNom || (status?.Abonnement ? 'Premium' : 'Utilisateur')}</p>
              <h2 className="mt-1 text-2xl font-black text-white">{status?.RaisonSociale || 'Espace personnel'}</h2>
            </div>
            <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${status?.EcritureAutorisee ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-amber-500/30 bg-amber-500/10 text-amber-100'}`}>
              {status?.EcritureAutorisee ? <CheckCircleIcon className="size-4" /> : <NoSymbolIcon className="size-4" />}
              {status?.EcritureAutorisee ? 'Fonctionnalités actives' : 'Lecture et téléchargements uniquement'}
            </span>
          </div>

          <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4"><dt className="text-slate-500">Véhicules actifs</dt><dd className="mt-1 text-lg font-bold">{status?.Quotas?.active?.count} / {status?.Quotas?.active?.max < 0 ? '∞' : status?.Quotas?.active?.max}</dd></div>
            <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4"><dt className="text-slate-500">Véhicules vendus</dt><dd className="mt-1 text-lg font-bold">{status?.Quotas?.sold?.count} / {status?.Quotas?.sold?.max < 0 ? '∞' : status?.Quotas?.sold?.max}</dd></div>
            <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4"><dt className="text-slate-500">Fin de période</dt><dd className="mt-1 font-bold">{formatDate(status?.Abonnement?.FinDate)}</dd></div>
            <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4"><dt className="text-slate-500">Temps restant</dt><dd className="mt-1 font-bold">{remainingDays} jour{remainingDays > 1 ? 's' : ''}</dd></div>
          </dl>

          {status?.EstCompteSupplementaire ? (
            <p className="mt-5 rounded-lg border border-white/10 bg-slate-900/60 p-4 text-sm text-slate-300">L’abonnement est administré par le compte professionnel principal.</p>
          ) : !canManageSubscription ? (
            <p className="mt-5 rounded-lg border border-white/10 bg-slate-900/60 p-4 text-sm text-slate-300">Ce grade interne ne nécessite pas d’abonnement commercial.</p>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {['MENSUEL', 'ANNUEL'].map((value) => (
                  <button key={value} type="button" onClick={() => { setPeriodicite(value); setPromoPreview(null); }} className={`rounded-lg border p-4 text-left ${periodicite === value ? 'border-sky-400 bg-sky-500/10' : 'border-white/10 bg-slate-900/60'}`}>
                    <span className="block text-sm font-bold text-white">{value === 'MENSUEL' ? 'Mensuel' : 'Annuel'}</span>
                    {value === 'MENSUEL' ? (
                      <span className="mt-1 block text-xs text-slate-400">{formatPrice(monthlyPriceCents)} / mois</span>
                    ) : (
                      <span className="mt-1 block text-xs">
                        {annualDiscountPercent > 0 && (
                          <span className="mr-2 text-rose-400 line-through decoration-rose-400">{formatPrice(annualBasePriceCents)}</span>
                        )}
                        <span className="font-bold text-emerald-300">{formatPrice(annualStandardPriceCents)} / an</span>
                        {annualDiscountPercent > 0 && <span className="mt-1 block text-slate-400">Réduction annuelle de {annualDiscountPercent} %</span>}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/5 p-4">
                <label className="text-sm font-bold text-fuchsia-100">Code promotionnel facultatif — simulation factice</label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input value={promoCode} onChange={(event) => { setPromoCode(event.target.value.toUpperCase()); setPromoPreview(null); }} placeholder="Ex. NOEL" className="min-w-0 flex-1 rounded-lg border border-fuchsia-500/30 bg-slate-950 px-3 py-2 text-white" />
                  <button type="button" disabled={!promoCode.trim() || promoLoading} onClick={validatePromoCode} className="rounded-lg border border-fuchsia-400/40 px-4 py-2 text-sm font-bold text-fuchsia-100 hover:bg-fuchsia-500/10 disabled:opacity-50">{promoLoading ? 'Vérification…' : 'Vérifier le code'}</button>
                </div>
                {activePromoPreview && (
                  <p className="mt-3 text-sm text-emerald-200">
                    Code <strong>{activePromoPreview.Code}</strong> : remise de {activePromoPreview.RemiseAppliqueePourcent} %, soit {(simulatedPriceCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € au lieu de {(activePromoPreview.PrixBaseCentimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € sur cette simulation.
                  </p>
                )}
                <p className="mt-2 text-xs leading-5 text-slate-500">Un code ne se cumule pas avec la réduction annuelle standard : seule la remise la plus avantageuse est retenue.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" disabled={!selectedPaymentMethodId} onClick={simulatePayment} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50">
                  <CreditCardIcon className="size-5" />
                  {status?.Abonnement ? `Simuler le renouvellement factice ${periodicite.toLowerCase()}` : `Simuler le paiement factice ${plan.toLowerCase()}`}
                </button>
                {status?.Abonnement && status.Abonnement.Statut !== 'RESILIE' && (
                  <button type="button" onClick={() => perform(() => api.post('/account/subscription/cancel'))} className="rounded-lg border border-rose-500/40 px-4 py-2.5 text-sm font-bold text-rose-100 hover:bg-rose-500/10">Résilier à la fin de la période</button>
                )}
              </div>
            </div>
          )}
        </article>

        <article className="rounded-xl border border-white/10 bg-slate-950/70 p-6">
          <h2 className="font-black text-white">Informations du compte</h2>
          <dl className="mt-4 space-y-3 text-sm">
            <div><dt className="text-slate-500">Type</dt><dd className="font-semibold">{status?.CompteType}</dd></div>
            {isProfessional && <><div><dt className="text-slate-500">SIRET</dt><dd className="font-semibold">{status?.Siret}</dd></div><div><dt className="text-slate-500">Téléphone</dt><dd className="font-semibold">{status?.Telephone}</dd></div></>}
            <div><dt className="text-slate-500">Statut</dt><dd className="font-semibold">{status?.Abonnement?.Statut || 'Gratuit'}</dd></div>
            {status?.Abonnement && <><div><dt className="text-slate-500">Périodicité simulée</dt><dd className="font-semibold">{status.Abonnement.Periodicite || 'MENSUEL'}</dd></div><div><dt className="text-slate-500">Tarif simulé</dt><dd className="font-semibold">{status.Abonnement.PrixFacticeCentimes == null ? '—' : `${(Number(status.Abonnement.PrixFacticeCentimes) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`} · factice</dd></div></>}
          </dl>
        </article>
      </section>

      {canManageSubscription && (
        <section className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
          <article className="rounded-xl border border-fuchsia-500/25 bg-slate-950/70 p-6">
            <div className="flex items-center gap-3"><CreditCardIcon className="size-5 text-fuchsia-300" /><h2 className="font-black text-white">Moyens de paiement factices</h2></div>
            <p className="mt-3 rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/10 p-3 text-xs leading-5 text-fuchsia-100">Démonstration uniquement : aucun débit, aucune vérification bancaire et aucune carte réelle. Seule l’option « Démo gratuite » fonctionne actuellement.</p>
            <div className="mt-4 space-y-3">
              {paymentMethods.length === 0 && <p className="text-sm text-slate-500">Ajoutez la Démo gratuite pour activer le paiement simulé.</p>}
              {paymentMethods.map((method) => (
                <label key={method.MoyenPaiementID} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-slate-900/60 p-4">
                  <span className="flex items-center gap-3"><input type="radio" name="paymentMethod" checked={String(method.MoyenPaiementID) === selectedPaymentMethodId} onChange={() => setSelectedPaymentMethodId(String(method.MoyenPaiementID))} /><span><span className="block font-bold text-white">{method.Libelle}</span><span className="text-xs text-slate-400">VISA DÉMO •••• {method.CarteDerniersChiffres} · factice</span></span></span>
                  <button type="button" onClick={() => perform(() => api.delete(`/account/payment-methods/${method.MoyenPaiementID}`))} className="rounded-md border border-rose-500/30 p-2 text-rose-200 hover:bg-rose-500/10" aria-label="Désactiver ce moyen de paiement factice"><TrashIcon className="size-4" /></button>
                </label>
              ))}
            </div>
          </article>

          <form onSubmit={createDemoPaymentMethod} className="rounded-xl border border-fuchsia-500/25 bg-slate-950/70 p-6">
            <h2 className="font-black text-white">Ajouter « Démo gratuite » — entièrement factice</h2>
            <p className="mt-2 text-xs text-slate-400">Les valeurs de démonstration sont préremplies. Le numéro complet et le cryptogramme ne seront jamais enregistrés.</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-300">Type de titulaire<select value={demoForm.titulaireType} onChange={(event) => setDemoForm((current) => ({ ...current, titulaireType: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white"><option value="PERSONNE">Personne physique — factice</option><option value="ORGANISME">Organisme — factice</option></select></label>
              <label className="text-sm font-semibold text-slate-300">Nom du titulaire<input value={demoForm.titulaireNom} onChange={(event) => setDemoForm((current) => ({ ...current, titulaireNom: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white" required /></label>
              {demoForm.titulaireType === 'ORGANISME' && <label className="text-sm font-semibold text-slate-300 sm:col-span-2">Raison sociale<input value={demoForm.raisonSociale} onChange={(event) => setDemoForm((current) => ({ ...current, raisonSociale: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white" required /></label>}
              {[
                ['email', 'Email factice', 'email'], ['telephone', 'Téléphone factice', 'tel'],
                ['adresseLigne1', 'Adresse factice', 'text'], ['adresseLigne2', 'Complément', 'text'],
                ['codePostal', 'Code postal', 'text'], ['ville', 'Ville', 'text'],
              ].map(([field, label, type]) => <label key={field} className="text-sm font-semibold text-slate-300">{label}<input type={type} value={demoForm[field]} onChange={(event) => setDemoForm((current) => ({ ...current, [field]: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white" required={field !== 'adresseLigne2'} /></label>)}
              <label className="text-sm font-semibold text-slate-300 sm:col-span-2">Carte de démonstration<input value={demoForm.numeroCarte} readOnly className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-slate-300" /></label>
              <label className="text-sm font-semibold text-slate-300">Mois d’expiration<input type="number" min="1" max="12" value={demoForm.expirationMois} onChange={(event) => setDemoForm((current) => ({ ...current, expirationMois: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white" required /></label>
              <label className="text-sm font-semibold text-slate-300">Année d’expiration<input type="number" value={demoForm.expirationAnnee} onChange={(event) => setDemoForm((current) => ({ ...current, expirationAnnee: event.target.value }))} className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-white" required /></label>
              <label className="text-sm font-semibold text-slate-300">Cryptogramme de démo<input value={demoForm.cryptogramme} readOnly className="mt-1 w-full rounded-lg border border-fuchsia-500/30 bg-slate-900 px-3 py-2 text-slate-300" /></label>
            </div>
            <button type="submit" className="mt-5 w-full rounded-lg bg-fuchsia-600 px-4 py-2.5 font-bold text-white hover:bg-fuchsia-500">Ajouter le moyen de paiement factice</button>
          </form>
        </section>
      )}

      {isOwner && (
        <section className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
          <form onSubmit={createMember} className="rounded-xl border border-sky-500/20 bg-slate-950/70 p-6">
            <div className="flex items-center gap-3"><PlusIcon className="size-5 text-sky-300" /><h2 className="font-black text-white">Créer un compte supplémentaire</h2></div>
            <p className="mt-2 text-xs text-slate-500">{members.length} / {memberMax} comptes utilisés</p>
            {['surnom', 'email', 'motDePasse'].map((field) => (
              <label key={field} className="mt-4 block text-sm font-semibold text-slate-300">
                {field === 'motDePasse' ? 'Mot de passe temporaire' : field === 'email' ? 'Email' : 'Surnom'}
                <input
                  type={field === 'motDePasse' ? 'password' : field === 'email' ? 'email' : 'text'}
                  value={form[field]}
                  onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
                  className="mt-1 w-full rounded-lg border border-sky-500/30 bg-slate-900 px-3 py-2 text-white outline-none focus:border-sky-400"
                  required
                />
              </label>
            ))}
            <button type="submit" disabled={memberMax >= 0 && members.length >= memberMax} className="mt-5 w-full rounded-lg bg-sky-600 px-4 py-2.5 font-bold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50">Créer le compte</button>
          </form>

          <article className="rounded-xl border border-white/10 bg-slate-950/70 p-6">
            <div className="flex items-center gap-3"><UserGroupIcon className="size-5 text-sky-300" /><h2 className="font-black text-white">Comptes supplémentaires</h2></div>
            <div className="mt-5 space-y-3">
              {members.length === 0 && <p className="text-sm text-slate-500">Aucun compte supplémentaire.</p>}
              {members.map((member) => (
                <div key={member.UtilisateurID} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-white/10 bg-slate-900/60 p-4">
                  <div><p className="font-bold text-white">{member.Surnom}</p><p className="text-sm text-slate-400">{member.Email}</p></div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => perform(() => api.put(`/account/members/${member.UtilisateurID}/state`, { blocked: member.EtatID === 1 }))} className={`rounded-md border px-3 py-1.5 text-xs font-bold ${member.EtatID === 1 ? 'border-amber-500/40 text-amber-100 hover:bg-amber-500/10' : 'border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/10'}`}>
                      {member.EtatID === 1 ? 'Bloquer' : 'Réactiver'}
                    </button>
                    <button type="button" onClick={() => window.confirm(`Supprimer ${member.Surnom} et transférer ses créations au compte principal ?`) && perform(() => api.delete(`/account/members/${member.UtilisateurID}`))} className="rounded-md border border-rose-500/40 p-2 text-rose-100 hover:bg-rose-500/10" aria-label={`Supprimer ${member.Surnom}`}><TrashIcon className="size-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      {activityJournalScope > 0 ? <section className="rounded-xl border border-white/10 bg-slate-950/70 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="font-black text-white">Journal des actions</h2><p className="mt-1 text-xs text-slate-500">{activityJournalScope === 1 ? 'Uniquement votre activité personnelle.' : 'Toute l’activité de l’organisation.'}</p></div><span className="text-xs font-semibold text-slate-500">50 dernières actions</span></div>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-white/10 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-3">Date</th><th className="px-3 py-3">Action</th><th className="px-3 py-3">Auteur</th><th className="px-3 py-3">Criticité</th><th className="px-3 py-3">Détails</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {history.map((item) => (
                <tr key={item.JournalActionID} className="text-slate-300"><td className="whitespace-nowrap px-3 py-3">{formatDate(item.CreateDate)}</td><td className="px-3 py-3 font-semibold text-white">{item.Action?.Nom}</td><td className="px-3 py-3">{item.ActeurSurnom || 'Système'}</td><td className="px-3 py-3"><span className={`rounded-full border px-2 py-1 text-xs font-bold ${item.Action?.Criticite === 'Critique' ? 'border-rose-500/30 bg-rose-500/10 text-rose-200' : item.Action?.Criticite === 'Attention' ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-sky-500/30 bg-sky-500/10 text-sky-200'}`}>{item.Action?.Criticite}</span></td><td className="max-w-sm px-3 py-3 text-xs text-slate-500"><details><summary className="cursor-pointer font-semibold text-slate-300">{item.CibleType ? `${item.CibleType}${item.CibleID ? ` #${item.CibleID}` : ''}` : 'Voir le détail'}</summary><p className="mt-2">{item.Action?.Description}</p>{item.Details && <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-slate-900 p-2 text-[10px] text-slate-400">{JSON.stringify(item.Details, null, 2)}</pre>}</details></td></tr>
              ))}
              {history.length === 0 && <tr><td colSpan="5" className="px-3 py-8 text-center text-slate-500">Aucune action enregistrée pour le moment.</td></tr>}
            </tbody>
          </table>
        </div>
      </section> : <section className="rounded-xl border border-white/10 bg-slate-950/70 p-6"><h2 className="font-black text-white">Journal d’activité</h2><p className="mt-2 text-sm text-slate-400">Le journal d’activité n’est pas inclus dans l’offre Utilisateur. Il devient personnel avec Premium.</p></section>}
    </main>
  );
};

export default SubscriptionPage;
